import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import SignupForm from './SignupForm.js';
// import SocialLogin from '../Components/SocialLogin.js';
import logo from '../../assets/images/logo-orange.png'

import { getCookie } from '../../util.js';

import {
  Card,
  Pane,
  Tablist,
  Tab,
  TextInputField,
  Button,
  Paragraph,
  Heading,
  DrawIcon,
  ShopIcon,
  BoxIcon,
  ChartIcon,
  LabTestIcon,
  CalculatorIcon,
  InfoSignIcon,
  Alert
} from 'evergreen-ui';

function Login(props) {
  const [email, setEmail] = useState('');
  const [pw, setPw] = useState('');
  const [selectedIndex, setSelectedIndex] = useState(0);
  const tabs = ['SIGN UP', 'LOGIN'];
  const [errors, setErrors] = useState(false);
  const [loading, setLoading] = useState(true);
  const [isMobile, setIsMobile] = useState(false);

  let navigate = useNavigate();

  // Add a mounted ref
  const isMounted = useRef(false);

  useEffect(() => {
    isMounted.current = true;

    // Check if device is mobile
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 768);
    };

    // Initial check
    checkMobile();

    // Add event listener for window resize
    window.addEventListener('resize', checkMobile);

    if (localStorage.getItem('token') !== null) {
      navigate('/dashboard');
    } else {
      setLoading(false);
    }

    return () => {
      isMounted.current = false;
      window.removeEventListener('resize', checkMobile);
    };
  }, []);


  const validateForm = () => {
    let valid = true;
    if (!email) {
      setErrors("Email is required.");
      valid = false;
    } else if (!pw) {
      setErrors("Password is required.");
      valid = false;
    } else {
      setErrors(false);
    }
    return valid;
  };

  const handleLogin = async (e) => {
    e.preventDefault();

    // Check if component is mounted
    if (!isMounted.current) return;

    if (!validateForm()) {
      return;
    }

    try {
      const csrftoken = getCookie('csrftoken');

      const user = {
        email: email,
        password: pw
      };

      const loginResponse = await fetch('/api/v1/auth/login/', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRFToken': csrftoken
        },
        body: JSON.stringify(user)
      });

      if (!loginResponse.ok) {
        const errorData = await loginResponse.json();
        throw new Error(
          errorData.non_field_errors?.[0] ||
          errorData.email?.[0] && `Email error: ${errorData.email[0]}` ||
          errorData.password?.[0] && `Password error: ${errorData.password[0]}` ||
          'Login failed. Please try again.'
        );
      }

      const data = await loginResponse.json();

      if (!data.key) {
        throw new Error('Invalid response from server');
      }

      localStorage.clear();
      localStorage.setItem('token', data.key);

      // Get publisher data
      const publisherResponse = await fetch('/fetch_user_publishers/', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRFToken': getCookie('csrftoken'),
          'Authorization': `Token ${data.key}`
        },
        body: JSON.stringify({ token: data.key })
      });

      if (!publisherResponse.ok) {
        const errorData = await publisherResponse.json().catch(() => ({}));
        throw new Error(errorData.error || 'Failed to fetch user data. Please try again.');
      }

      const publisherData = await publisherResponse.json();

      if (!publisherData.user || !publisherData.publisher) {
        throw new Error('Invalid user data received');
      }

      localStorage.setItem('userid', publisherData.user.id);
      localStorage.setItem('publisher', publisherData.publisher.id);
      localStorage.setItem('publisher_name', publisherData.publisher.name);

      // Only redirect if still mounted
      if (isMounted.current) {
        window.location.replace('/dashboard');
      }

    } catch (error) {
      console.error('Login error:', error);
      if (isMounted.current) {
        setEmail('');
        setPw('');
        localStorage.clear();
        setErrors(error.message || "An unexpected error occurred. Please try again.");
      }
    }
  };

  return (
    <Pane backgroundColor="var(--darkgreen)" display="flex" flexDirection="column" alignItems="center" width="100vw" paddingBottom={32} >
      {isMobile && (
        <Alert
          intent="warning"
          position="fixed"
          top={0}
          left={0}
          right={0}
          zIndex={100}
          backgroundColor="var(--maroon)"
          title={
            <Pane display="flex" alignItems="center">
              <InfoSignIcon color="white" marginRight={8} />
              <Paragraph color="white" size={300}>
                For the best experience, please use WorkingLit on a desktop browser.
              </Paragraph>
            </Pane>
          }
        />
      )}
      <Pane backgroundColor="var(--darkgreen)" paddingTop={isMobile ? 48 : 16}>
        <img
          className="logo-small"
          src={logo}
          alt="working-lit-logo"
          style={{ maxWidth: isMobile ? '200px' : 'none' }}
        />
      </Pane>

      <Card
        display="flex"
        flexDirection={isMobile ? "column" : "row"}
        border="muted"
        borderWidth={3}
        marginY={16}
        width={isMobile ? "95vw" : "80vw"}
        elevation={4}
      >
        <Pane
          width={isMobile ? "100%" : "55%"}
          backgroundColor="var(--lightgrey)"
          paddingY={16}
          paddingX={isMobile ? 16 : 40}
          borderTopLeftRadius={5}
          borderBottomLeftRadius={isMobile ? 0 : 5}
          borderTopRightRadius={isMobile ? 5 : 0}
          display="flex"
          flexDirection="column"
        >
          <Heading
            size={isMobile ? 700 : 900}
            marginTop={32}
            lineHeight={1.4}
          >
            WorkingLit gives book publishers the tools to flourish. Stay as independent as you want to be while you grow your business and reach readers.
          </Heading>

          <Pane marginLeft={isMobile ? 16 : 32} marginTop={40}>
            {[
              { icon: DrawIcon, text: "Manage product and author metadata" },
              { icon: BoxIcon, text: "Track sales and expenses" },
              { icon: ShopIcon, text: "Manage customer account data" },
              { icon: CalculatorIcon, text: "Calculate royalties and track payments to authors" },
              { icon: LabTestIcon, text: "Understand the health of your business" },
              { icon: ChartIcon, text: "Make decisions that help you grow" }
            ].map(({ icon: Icon, text }, index) => (
              <Pane
                key={index}
                display="flex"
                alignItems="flex-start"
                marginBottom={isMobile ? 24 : 32}
              >
                <Icon
                  marginRight={24}
                  size={isMobile ? 16 : 20}
                  color="var(--maroon)"
                />
                <Heading
                  color="var(--maroon)"
                  size={isMobile ? 500 : 700}
                  lineHeight={1.3}
                >
                  {text}
                </Heading>
              </Pane>
            ))}
            <Pane display="flex">
              <Heading
                color="var(--maroon)"
                size={isMobile ? 500 : 700}
                marginBottom={32}
              >
                <a href="https://www.workinglit.com" target="_blank" rel="noreferrer">
                  Learn more about WorkingLit
                </a>
              </Heading>
            </Pane>
          </Pane>
        </Pane>

        <Pane
          id="login-container"
          backgroundColor="var(--lightgreen)"
          width={isMobile ? "100%" : "45%"}
          paddingY={16}
          paddingX={isMobile ? 16 : 32}
          borderTopRightRadius={isMobile ? 0 : 5}
          borderBottomRightRadius={5}
          borderBottomLeftRadius={isMobile ? 5 : 0}
        >
          <Tablist marginBottom={isMobile ? 16 : 0}>
            {tabs.map((tab, index) => (
              <Tab
                key={tab.replace(" ", "")}
                id={tab.replace(" ", "")}
                className="login-tab"
                onSelect={() => setSelectedIndex(index)}
                isSelected={index === selectedIndex}
                aria-controls={`panel-${tab.replace(" ", "")}`}
                fontSize={isMobile ? "14px" : "16px"}
              >
                {tab}
              </Tab>
            ))}
          </Tablist>
          {tabs.map((tab, index) => (
            <Pane
              key={tab.replace(" ", "")}
              id={`panel-${tab.replace(" ", "")}`}
              role="tabpanel"
              aria-labelledby={tab}
              aria-hidden={index !== selectedIndex}
              display={index === selectedIndex ? 'block' : 'none'}
              marginY={0}
            >
              {tab === "SIGN UP" ? (
                <Pane display="flex" flexDirection="column">
                  <SignupForm emailmode={true} mode="signup" pubid={false} />
                </Pane>
              ) : (
                <Pane display="flex" flexDirection="column" paddingY={16}>
                  <Heading
                    marginBottom={8}
                    size={isMobile ? 600 : 700}
                    color="var(--darkgreen)"
                    marginTop={32}
                  >
                    Log in to your Account
                  </Heading>
                  <Paragraph marginBottom={32} size={300}>Welcome back!</Paragraph>

                  <TextInputField
                    required={false}
                    label="Email Address"
                    onChange={e => setEmail(e.target.value)}
                    onKeyPress={e => { if (e.key === 'Enter') handleLogin(e); }}
                    value={email}
                    type="email"
                    name="login-email"
                    height={isMobile ? 32 : 40}
                  />
                  <TextInputField
                    required={false}
                    label="Password"
                    onChange={e => setPw(e.target.value)}
                    onKeyPress={e => { if (e.key === 'Enter') handleLogin(e); }}
                    value={pw}
                    type="password"
                    name="login-password"
                    height={isMobile ? 32 : 40}
                  />

                  {errors && <Paragraph color="red">{errors}</Paragraph>}
                  <Button
                    className="confirm-button"
                    onClick={handleLogin}
                    height={isMobile ? 32 : 40}
                  >
                    LOG IN
                  </Button>
                  <Paragraph marginTop={16}>
                    <a href="/reset_password" style={{ color: 'var(--darkgreen)' }}>
                      Forgot Password?
                    </a>
                  </Paragraph>
                </Pane>
              )}
            </Pane>
          ))}
        </Pane>
      </Card>
    </Pane>
  )
}

export default Login;
